import { useMemo, useState, useCallback } from "react";
import DatagridV2 from "ui-kit/src/Datagrid/DatagridV2";
import client from "libs/client";
import {
	countriesColumns,
	countriesFilters,
	countriesBackendAdapter,
	type CountriesRow,
} from "../datagrid-configs/operationsManagementSummary/countries";
import { ColumnVisibilityModal } from "../datagrid/ColumnVisibilityModal";
import { ColorGradingModal } from "../datagrid/ColorGradingModal";
import { DatagridFilters } from "../datagrid/DatagridFilters";
import { ExportModal, type ExportPeriod } from "../datagrid/ExportModal";
import { triggerExportDownload } from "../datagrid/exportUtils";
import { useOperationsManagementTable } from "./hooks/useOperationsManagementTable";
import { useCountryFilterOptions } from "./hooks/useCountryFilterOptions";
import { useI18n } from "features/translation/context";
import { COUNTRIES_PERCENTAGE_COLUMNS } from "../datagrid/colorGradingUtils";
import "./OperationsManagementSummaryTableV3.css";

interface CountriesTableProps {
	/** Storage key prefix for localStorage persistence */
	storageKey?: string;
	/** Initial column visibility overrides */
	initialColumnVisibility?: Record<string, boolean>;
	/** Callback when a country row is clicked */
	onCountryClick?: (countryCode: string) => void;
}

/**
 * Countries tab table component.
 * Displays aggregated data per Country.
 */
export function CountriesTable({
	storageKey = "operationsManagementSummary_countries",
	initialColumnVisibility = {},
	onCountryClick,
}: CountriesTableProps) {
	const { _, locale } = useI18n();
	const tableColumns = useMemo(
		() =>
			countriesColumns.map((col) =>
				col.key === "subscriptionsSold"
					? { ...col, label: _("admin.summary_tab.label.active_subscription") }
					: col
			),
		[_, locale]
	);
	const tableFilters = useMemo(
		() =>
			countriesFilters.map((f) =>
				f.id === "subscriptionsSold"
					? {
							...f,
							label: _("admin.summary_tab.label.active_subscription"),
							placeholder: _("admin.summary_tab.placeholder.active_subscription"),
						}
					: f
			),
		[_, locale]
	);
	// Fetch dynamic filter options from API
	const { dynamicOptions } = useCountryFilterOptions(tableFilters);

	// First, get basic hook state without data-dependent values
	const {
		pagination,
		setPagination,
		sort,
		handleSort,
		filterValues,
		filtersCollapsed,
		handleFilterChange,
		handleClearFilters,
		handleToggleFiltersCollapse,
		visibleColumns,
		defaultVisibility,
		columnOrder,
		defaultColumnOrder,
		showColumnModal,
		setShowColumnModal,
		handleVisibilityChange,
		customViews,
		selectedCustomViewId,
		selectedViewName,
		handleCustomViewsChange,
		groupColors,
		colorGradingConfig,
		showColorGradingModal,
		setShowColorGradingModal,
		handleColorGradingChange,
		columnGroups,
		apiFilters,
		apiSort,
		apiColumns,
		columnWidths,
		handleColumnWidthsChange,
	} = useOperationsManagementTable<CountriesRow>(
		{
			columns: tableColumns,
			filters: tableFilters,
			backendAdapter: countriesBackendAdapter,
			storageKey,
			initialColumnVisibility,
			defaultPageSize: 50,
			percentageColumns: COUNTRIES_PERCENTAGE_COLUMNS,
		},
		[] // Initial empty array
	);

	// Memoize params so the same object ref is passed when values are unchanged (avoids extra fetches from new key each render).
	const apiColumnsKey = JSON.stringify(apiColumns);
	const apiFiltersKey = JSON.stringify(apiFilters);
	const queryParams = useMemo(
		() => ({
			page: pagination.pageIndex + 1,
			limit: pagination.pageSize,
			columns: apiColumns.length > 0 ? apiColumns : undefined,
			filters: apiFilters.length > 0 ? apiFilters : undefined,
			order: apiSort ? { field: apiSort.field, direction: apiSort.direction as "ASC" | "DESC" } : undefined,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- apiColumnsKey/apiFiltersKey for stable comparison
		[pagination.pageIndex, pagination.pageSize, apiColumnsKey, apiFiltersKey, apiSort?.field, apiSort?.direction]
	);

	// Fetch data from API
	const { data, isLoading, isError, refetch } = client.getOperationsManagementSummaryByCountry.useQuery(
		queryParams,
		{ retry: false }
	);

	// Transform API response to table data
	const tableData = useMemo(() => {
		if (!data?.data?.items) return [];
		return data.data.items as CountriesRow[];
	}, [data]);

	// Re-run hook with actual data to get correct simpleColumns with totals
	// Pass external groupColors, columnOrder, sort, and colorGrading to ensure state is shared
	const { simpleColumns } = useOperationsManagementTable<CountriesRow>(
		{
			columns: tableColumns,
			filters: tableFilters,
			backendAdapter: countriesBackendAdapter,
			storageKey,
			initialColumnVisibility,
			defaultPageSize: 50,
			externalGroupColors: groupColors,
			externalColumnOrder: columnOrder,
			externalSort: sort,
			externalHandleSort: handleSort,
			externalColorGradingConfig: colorGradingConfig,
			externalHandleColorGradingChange: handleColorGradingChange,
			externalColumnWidths: columnWidths,
			externalHandleColumnWidthsChange: handleColumnWidthsChange,
			percentageColumns: COUNTRIES_PERCENTAGE_COLUMNS,
		},
		tableData // Pass actual data for footer totals calculation
	);

	// Calculate page count from API response
	const pageCount = data?.data?.pages ?? 0;
	const totalItems = data?.data?.total ?? 0;

	// Export state
	const [showExportModal, setShowExportModal] = useState(false);

	// Memoize retry handler to avoid unnecessary DatagridV2 re-renders
	const handleRetry = useCallback(() => refetch(), [refetch]);

	// Country navigation only on the Country cell (not the whole row — DatagridV2 rowAction clicks the <tr>)
	const datagridColumns = useMemo(() => {
		if (!onCountryClick) {
			return simpleColumns;
		}
		return simpleColumns.map((col) => {
			if (col.key !== "country" || !col.cell) {
				return col;
			}
			const originalCell = col.cell;
			return {
				...col,
				cell: (value: CountriesRow[keyof CountriesRow], row: CountriesRow) => {
					const countryCode = (row.country as string) || (row.countryName as string);
					const content = originalCell(value, row);
					if (countryCode === "" || countryCode == null) {
						return content;
					}
					return (
						<button
							type="button"
							className="operationsManagementSummaryTableV3-countryCellBtn"
							onClick={(e) => {
								e.stopPropagation();
								onCountryClick(countryCode);
							}}
						>
							{content}
						</button>
					);
				},
			};
		});
	}, [simpleColumns, onCountryClick]);

	// Export handler - uses backend Excel generation
	const handleExport = useCallback(
		(_period: ExportPeriod) => {
			// Get visible column keys in order
			const visibleColumnKeys = columnOrder.filter((key) => visibleColumns[key] !== false);

			// Trigger backend export download
			triggerExportDownload("/admin/operations-management/summary/by-country/export", {
				columns: visibleColumnKeys,
				filters: apiFilters.length > 0 ? apiFilters : undefined,
				order: apiSort ? { field: apiSort.field, direction: apiSort.direction as "ASC" | "DESC" } : undefined,
			});

			setShowExportModal(false);
		},
		[apiFilters, apiSort, columnOrder, visibleColumns]
	);

	return (
		<div className="discover-card discover-card--for-table">
			<div className="operationsManagementSummaryTableV3">
				{/* Header with Filters and Action buttons */}
				<div
					className={`operationsManagementSummaryTableV3-header ${filtersCollapsed ? "filters-collapsed" : ""
						}`}
				>
					{/* Top row: Filters (shown when expanded) */}
					{!filtersCollapsed && (
						<div className="operationsManagementSummaryTableV3-headerTop">
							<div className="operationsManagementSummaryTableV3-headerLeft">
								<DatagridFilters
									filters={tableFilters}
									filterValues={filterValues}
									onFilterChange={handleFilterChange}
									onClear={handleClearFilters}
									isCollapsed={filtersCollapsed}
									onToggleCollapse={handleToggleFiltersCollapse}
									dynamicOptions={dynamicOptions}
									columnVisibility={visibleColumns}
								/>
							</div>
						</div>
					)}

					{/* Single row: Filters toggle + Action buttons (shown when collapsed) */}
					{filtersCollapsed && (
						<div className="operationsManagementSummaryTableV3-headerSingleRow">
							<div className="operationsManagementSummaryTableV3-headerLeft">
								<DatagridFilters
									filters={tableFilters}
									filterValues={filterValues}
									onFilterChange={handleFilterChange}
									onClear={handleClearFilters}
									isCollapsed={filtersCollapsed}
									onToggleCollapse={handleToggleFiltersCollapse}
									dynamicOptions={dynamicOptions}
									columnVisibility={visibleColumns}
								/>
							</div>
							<div className="operationsManagementSummaryTableV3-headerRight">
								<button
									type="button"
									className="operationsManagementSummaryTableV3-columnBtn"
									onClick={() => setShowColorGradingModal(true)}
									title="Configure color grading for percentage columns"
								>
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<rect x="1" y="3" width="14" height="2" rx="1" fill="#ef4444" />
										<rect x="1" y="7" width="14" height="2" rx="1" fill="#eab308" />
										<rect x="1" y="11" width="14" height="2" rx="1" fill="#22c55e" />
									</svg>
									Color grading
								</button>
								<button
									type="button"
									className="operationsManagementSummaryTableV3-columnBtn"
									onClick={() => setShowColumnModal(true)}
									title="Configure column visibility"
								>
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M2 4h12M2 8h12M2 12h12"
											stroke="currentColor"
											strokeWidth="1.5"
											strokeLinecap="round"
										/>
										<circle cx="5" cy="4" r="1.5" fill="currentColor" />
										<circle cx="11" cy="8" r="1.5" fill="currentColor" />
										<circle cx="7" cy="12" r="1.5" fill="currentColor" />
									</svg>
									Column visibility
									{selectedViewName && (
										<span className="operationsManagementSummaryTableV3-viewBadge">{selectedViewName}</span>
									)}
								</button>
								<button
									type="button"
									className="operationsManagementSummaryTableV3-columnBtn operationsManagementSummaryTableV3-exportBtn"
									onClick={() => setShowExportModal(true)}
									title="Export data to Excel"
								>
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M2 10v3a1 1 0 001 1h10a1 1 0 001-1v-3M8 2v8M5 7l3 3 3-3"
											stroke="currentColor"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
									Export
								</button>
							</div>
						</div>
					)}

					{/* Bottom row: Action buttons (shown when filters expanded) */}
					{!filtersCollapsed && (
						<div className="operationsManagementSummaryTableV3-headerBottom">
							<button
								type="button"
								className="operationsManagementSummaryTableV3-columnBtn"
								onClick={() => setShowColorGradingModal(true)}
								title="Configure color grading for percentage columns"
							>
								<svg
									width="16"
									height="16"
									viewBox="0 0 16 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<rect x="1" y="3" width="14" height="2" rx="1" fill="#ef4444" />
									<rect x="1" y="7" width="14" height="2" rx="1" fill="#eab308" />
									<rect x="1" y="11" width="14" height="2" rx="1" fill="#22c55e" />
								</svg>
								Color grading
							</button>
							<button
								type="button"
								className="operationsManagementSummaryTableV3-columnBtn"
								onClick={() => setShowColumnModal(true)}
								title="Configure column visibility"
							>
								<svg
									width="16"
									height="16"
									viewBox="0 0 16 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M2 4h12M2 8h12M2 12h12"
										stroke="currentColor"
										strokeWidth="1.5"
										strokeLinecap="round"
									/>
									<circle cx="5" cy="4" r="1.5" fill="currentColor" />
									<circle cx="11" cy="8" r="1.5" fill="currentColor" />
									<circle cx="7" cy="12" r="1.5" fill="currentColor" />
								</svg>
								Column visibility
								{selectedViewName && (
									<span className="operationsManagementSummaryTableV3-viewBadge">{selectedViewName}</span>
								)}
							</button>
							<button
								type="button"
								className="operationsManagementSummaryTableV3-columnBtn operationsManagementSummaryTableV3-exportBtn"
								onClick={() => setShowExportModal(true)}
								title="Export data to Excel"
							>
								<svg
									width="16"
									height="16"
									viewBox="0 0 16 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M2 10v3a1 1 0 001 1h10a1 1 0 001-1v-3M8 2v8M5 7l3 3 3-3"
										stroke="currentColor"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
								Export
							</button>
						</div>
					)}
				</div>

				{/* Table */}
				<div className="operationsManagementSummaryTableV3-table">
					<DatagridV2<CountriesRow>
						noResults="No data found"
						data={tableData}
						simpleColumns={datagridColumns}
						pagination={pagination}
						setPagination={setPagination}
						pageCount={pageCount}
						isLoading={isLoading}
						isError={isError}
						onRetry={handleRetry}
						columnVisibility={visibleColumns}
						showFooter
						showPageSizeSelector
						totalItems={data?.data?.total}
						onColumnWidthsChange={handleColumnWidthsChange}
						autoSizeColumns
						stickyHeader
					/>
				</div>

				{/* Column Visibility Modal */}
				{showColumnModal && (
					<ColumnVisibilityModal
						columnGroups={columnGroups}
						columnVisibility={visibleColumns}
						defaultVisibility={defaultVisibility}
						onVisibilityChange={handleVisibilityChange}
						onClose={() => setShowColumnModal(false)}
						columnOrder={columnOrder}
						defaultColumnOrder={defaultColumnOrder}
						customViews={customViews}
						selectedCustomViewId={selectedCustomViewId}
						onCustomViewsChange={handleCustomViewsChange}
						groupColors={groupColors}
					/>
				)}

				{/* Color Grading Modal */}
				{showColorGradingModal && (
					<ColorGradingModal
						percentageColumns={COUNTRIES_PERCENTAGE_COLUMNS}
						colorGradingConfig={colorGradingConfig}
						onConfigChange={handleColorGradingChange}
						onClose={() => setShowColorGradingModal(false)}
						columnVisibility={visibleColumns}
					/>
				)}

				{/* Export Modal */}
				{showExportModal && (
					<ExportModal
						onExport={handleExport}
						onClose={() => setShowExportModal(false)}
						totalRows={totalItems}
					/>
				)}
			</div>
		</div>
	);
}

export default CountriesTable;
